import React from 'react'
import { motion } from 'framer-motion'

interface EmptyStateIllustrationProps {
  type: 'query-params' | 'headers' | 'body' | 'response'
  title: string
  description: string
  className?: string
}

const EmptyStateIllustration: React.FC<EmptyStateIllustrationProps> = ({
  type,
  title,
  description,
  className = ''
}) => {


  const getIllustration = () => {
    const commonProps = {
      className: "w-40 h-40 mb-6",
      viewBox: "0 0 240 240",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      initial: { opacity: 0, scale: 0.8 },
      animate: { opacity: 1, scale: 1 },
      transition: { duration: 0.6, ease: "easeOut" }
    }

    switch (type) {
      case 'query-params':
        return (
          <motion.svg {...commonProps}>
            {/* Unified background with subtle gradient */}
            <motion.rect
              x="20"
              y="20"
              width="200"
              height="200"
              rx="20"
              fill="url(#unifiedBgGradient)"
              stroke="url(#unifiedBorderGradient)"
              strokeWidth="2"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            />
            
            {/* URL Bar representation */}
            <motion.rect
              x="40"
              y="60"
              width="160"
              height="24"
              rx="12"
              fill="currentColor"
              className="text-blue-100 dark:text-blue-900"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            />
            
            {/* URL Path */}
            <motion.text
              x="50"
              y="76"
              className="text-xs font-mono fill-blue-600 dark:fill-blue-400"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.4 }}
            >
              /api/endpoint
            </motion.text>
            
            {/* Question mark - central focus */}
            <motion.circle
              cx="120"
              cy="130"
              r="20"
              fill="url(#queryIconGradient)"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.6, duration: 0.5, type: "spring", stiffness: 200 }}
            />
            
            <motion.text
              x="120"
              y="138"
              textAnchor="middle"
              className="text-xl font-bold fill-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.4 }}
            >
              ?
            </motion.text>
            
            {/* Parameter key-value pairs with improved animation */}
            {[
              { x: 60, y: 170, key: "key1", value: "value1" },
              { x: 120, y: 170, key: "key2", value: "value2" },
              { x: 180, y: 170, key: "key3", value: "value3" }
            ].map((param, i) => (
              <motion.g key={i}>
                <motion.rect
                  x={param.x - 15}
                  y={param.y - 8}
                  width="30"
                  height="16"
                  rx="8"
                  fill="currentColor"
                  className="text-blue-200 dark:text-blue-800"
                  initial={{ y: param.y + 20, opacity: 0 }}
                  animate={{ 
                    y: param.y - 8, 
                    opacity: [0, 1, 0.8, 1],
                  }}
                  transition={{
                    delay: 1.2 + i * 0.2,
                    duration: 0.6,
                    repeat: Infinity,
                    repeatType: "reverse",
                    repeatDelay: 4
                  }}
                />
                <motion.text
                  x={param.x}
                  y={param.y + 2}
                  textAnchor="middle"
                  className="text-xs font-mono fill-blue-600 dark:fill-blue-400"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.4 + i * 0.2, duration: 0.4 }}
                >
                  {param.key}
                </motion.text>
              </motion.g>
            ))}
            
            
            <defs>
              <linearGradient id="unifiedBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(239, 246, 255)" stopOpacity="0.6" />
                <stop offset="100%" stopColor="rgb(219, 234, 254)" stopOpacity="0.8" />
              </linearGradient>
              <linearGradient id="unifiedBorderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="rgb(147, 51, 234)" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="queryIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(59, 130, 246)" />
                <stop offset="100%" stopColor="rgb(37, 99, 235)" />
              </linearGradient>
            </defs>
          </motion.svg>
        )
      
      case 'headers':
        return (
          <motion.svg {...commonProps}>
            {/* Unified background */}
            <motion.rect
              x="20"
              y="20"
              width="200"
              height="200"
              rx="20"
              fill="url(#headersBgGradient)"
              stroke="url(#headersBorderGradient)"
              strokeWidth="2"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            />
            
            {/* Envelope/Document shape */}
            <motion.rect
              x="60"
              y="80"
              width="120"
              height="80"
              rx="8"
              fill="currentColor"
              className="text-green-100 dark:text-green-900"
              stroke="currentColor"
              strokeWidth="2"
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 80, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            />
            
            {/* Header icon in center */}
            <motion.circle
              cx="120"
              cy="120"
              r="16"
              fill="url(#headersIconGradient)"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.8, duration: 0.5, type: "spring" }}
            />
            
            <motion.text
              x="120"
              y="126"
              textAnchor="middle"
              className="text-sm font-bold fill-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.4 }}
            >
              H
            </motion.text>
            
            {/* Header lines with staggered animation */}
            {[...Array(4)].map((_, i) => (
              <motion.line
                key={i}
                x1="70"
                y1={90 + i * 12}
                x2={140 + i * 8}
                y2={90 + i * 12}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className="text-green-500 dark:text-green-400"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.7 }}
                transition={{ delay: 0.6 + i * 0.1, duration: 0.8 }}
              />
            ))}
            
            {/* Key-value representation */}
            <motion.text
              x="120"
              y="180"
              textAnchor="middle"
              className="text-xs font-mono fill-green-600 dark:fill-green-400"
              initial={{ opacity: 0, y: 190 }}
              animate={{ opacity: 1, y: 180 }}
              transition={{ delay: 1.4, duration: 0.4 }}
            >
              Content-Type: application/json
            </motion.text>
            
            
            <defs>
              <linearGradient id="headersBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(240, 253, 244)" stopOpacity="0.6" />
                <stop offset="100%" stopColor="rgb(220, 252, 231)" stopOpacity="0.8" />
              </linearGradient>
              <linearGradient id="headersBorderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(34, 197, 94)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="headersIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(34, 197, 94)" />
                <stop offset="100%" stopColor="rgb(22, 163, 74)" />
              </linearGradient>
            </defs>
          </motion.svg>
        )
      
      case 'body':
        return (
          <motion.svg {...commonProps}>
            {/* Unified background */}
            <motion.rect
              x="20"
              y="20"
              width="200"
              height="200"
              rx="20"
              fill="url(#bodyBgGradient)"
              stroke="url(#bodyBorderGradient)"
              strokeWidth="2"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            />
            
            {/* JSON container */}
            <motion.rect
              x="60"
              y="80"
              width="120"
              height="80"
              rx="8"
              fill="currentColor"
              className="text-purple-100 dark:text-purple-900"
              stroke="currentColor"
              strokeWidth="2"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            />
            
            {/* JSON braces with improved positioning */}
            <motion.text
              x="75"
              y="95"
              className="text-lg font-bold fill-purple-600 dark:fill-purple-400"
              initial={{ opacity: 0, x: 65 }}
              animate={{ opacity: 1, x: 75 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            >
              {'{'}
            </motion.text>
            
            <motion.text
              x="165"
              y="155"
              className="text-lg font-bold fill-purple-600 dark:fill-purple-400"
              initial={{ opacity: 0, x: 175 }}
              animate={{ opacity: 1, x: 165 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              {'}'}
            </motion.text>
            
            {/* JSON icon in center */}
            <motion.circle
              cx="120"
              cy="120"
              r="14"
              fill="url(#bodyIconGradient)"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.7, duration: 0.5, type: "spring" }}
            />
            
            <motion.text
              x="120"
              y="126"
              textAnchor="middle"
              className="text-xs font-bold fill-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.4 }}
            >
              { }
            </motion.text>
            
            {/* JSON key-value pairs with enhanced animation */}
            {[
              { x: 90, y: 105, label: '"key"' },
              { x: 150, y: 105, label: '"value"' },
              { x: 90, y: 135, label: '"data"' },
              { x: 150, y: 135, label: '"array"' }
            ].map((item, i) => (
              <motion.g key={i}>
                <motion.text
                  x={item.x}
                  y={item.y}
                  textAnchor="middle"
                  className="text-xs font-mono fill-purple-600 dark:fill-purple-400"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ 
                    opacity: [0, 1, 0.7, 1],
                    scale: [0.5, 1, 0.9, 1]
                  }}
                  transition={{
                    delay: 1 + i * 0.2,
                    duration: 0.8,
                    repeat: Infinity,
                    repeatType: "reverse",
                    repeatDelay: 3
                  }}
                >
                  {item.label}
                </motion.text>
              </motion.g>
            ))}
            
            {/* Floating data particles */}
            {[...Array(6)].map((_, i) => (
              <motion.circle
                key={i}
                cx={80 + Math.random() * 80}
                cy={90 + Math.random() * 60}
                r="2"
                fill="currentColor"
                className="text-purple-400 dark:text-purple-300"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ 
                  opacity: [0, 1, 0.5, 0],
                  scale: [0, 1, 1.2, 0],
                  x: [0, (Math.random() - 0.5) * 20],
                  y: [0, (Math.random() - 0.5) * 20]
                }}
                transition={{
                  delay: 1.5 + i * 0.3,
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "loop"
                }}
              />
            ))}
            
            
            <defs>
              <linearGradient id="bodyBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(250, 245, 255)" stopOpacity="0.6" />
                <stop offset="100%" stopColor="rgb(243, 232, 255)" stopOpacity="0.8" />
              </linearGradient>
              <linearGradient id="bodyBorderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(147, 51, 234)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="rgb(236, 72, 153)" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="bodyIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(147, 51, 234)" />
                <stop offset="100%" stopColor="rgb(126, 34, 206)" />
              </linearGradient>
            </defs>
          </motion.svg>
        )
      
      case 'response':
        return (
          <motion.svg {...commonProps}>
            {/* Unified background */}
            <motion.rect
              x="20"
              y="20"
              width="200"
              height="200"
              rx="20"
              fill="url(#responseBgGradient)"
              stroke="url(#responseBorderGradient)"
              strokeWidth="2"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            />
            
            {/* Response container */}
            <motion.rect
              x="60"
              y="80"
              width="120"
              height="80"
              rx="8"
              fill="currentColor"
              className="text-orange-100 dark:text-orange-900"
              stroke="currentColor"
              strokeWidth="2"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            />
            
            {/* Response icon in center */}
            <motion.circle
              cx="120"
              cy="120"
              r="16"
              fill="url(#responseIconGradient)"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.8, duration: 0.5, type: "spring" }}
            />
            
            <motion.text
              x="120"
              y="126"
              textAnchor="middle"
              className="text-sm font-bold fill-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.4 }}
            >
              R
            </motion.text>
            
            {/* Response status indicators */}
            {[
              { x: 80, y: 100, status: "200" },
              { x: 120, y: 90, status: "OK" },
              { x: 160, y: 100, status: "JSON" }
            ].map((item, i) => (
              <motion.text
                key={i}
                x={item.x}
                y={item.y}
                textAnchor="middle"
                className="text-xs font-mono fill-orange-600 dark:fill-orange-400"
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ 
                  opacity: [0, 1, 0.8, 1],
                  scale: [0.5, 1, 0.9, 1]
                }}
                transition={{
                  delay: 1 + i * 0.2,
                  duration: 0.8,
                  repeat: Infinity,
                  repeatType: "reverse",
                  repeatDelay: 3
                }}
              >
                {item.status}
              </motion.text>
            ))}
            
            {/* Response data flow lines */}
            {[...Array(3)].map((_, i) => (
              <motion.line
                key={i}
                x1="70"
                y1={130 + i * 8}
                x2={150 + i * 6}
                y2={130 + i * 8}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className="text-orange-500 dark:text-orange-400"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.7 }}
                transition={{ delay: 0.6 + i * 0.1, duration: 0.8 }}
              />
            ))}
            
            {/* Floating response particles */}
            {[...Array(4)].map((_, i) => (
              <motion.circle
                key={i}
                cx={80 + Math.random() * 80}
                cy={170 + Math.random() * 30}
                r="2"
                fill="currentColor"
                className="text-orange-400 dark:text-orange-300"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ 
                  opacity: [0, 1, 0.6, 0],
                  scale: [0, 1, 1.1, 0],
                  x: [0, (Math.random() - 0.5) * 15],
                  y: [0, (Math.random() - 0.5) * 15]
                }}
                transition={{
                  delay: 1.3 + i * 0.3,
                  duration: 2.5,
                  repeat: Infinity,
                  repeatType: "loop"
                }}
              />
            ))}
            
            {/* Status text */}
            <motion.text
              x="120"
              y="180"
              textAnchor="middle"
              className="text-xs font-mono fill-orange-600 dark:fill-orange-400"
              initial={{ opacity: 0, y: 190 }}
              animate={{ opacity: 1, y: 180 }}
              transition={{ delay: 1.4, duration: 0.4 }}
            >
              Waiting for response...
            </motion.text>
            
            <defs>
              <linearGradient id="responseBgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(255, 247, 237)" stopOpacity="0.6" />
                <stop offset="100%" stopColor="rgb(254, 237, 227)" stopOpacity="0.8" />
              </linearGradient>
              <linearGradient id="responseBorderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(251, 146, 60)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="rgb(245, 101, 101)" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="responseIconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="rgb(251, 146, 60)" />
                <stop offset="100%" stopColor="rgb(234, 88, 12)" />
              </linearGradient>
            </defs>
          </motion.svg>
        )
      
      default:
        return null
    }
  }

  return (
    <div className={`flex flex-col items-center justify-center py-16 ${className}`}>
      {getIllustration()}
      <motion.h3 
        className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-3"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.6, duration: 0.5 }}
      >
        {title}
      </motion.h3>
      <motion.p 
        className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-sm leading-relaxed"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8, duration: 0.5 }}
      >
        {description}
      </motion.p>
    </div>
  )
}

export default EmptyStateIllustration
